import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { Module } from '../modules/services/firmware/firmware.service';
import { AppsColumnViewComponentTooltip } from './apps-column-view-tooltip/apps-column-view-tooltip.component';
import { CohortsColumnViewToolTipComponent } from './cohorts-column-view-tooltip/cohorts-column-view-tooltip.component';
import { CohortsColumnViewComponent } from './cohorts-column-view/cohorts-column-view.component';
import { CreateReleaseComponent } from './create-release/create-release.component';
import { CurrentPhaseComponent } from './current-phase/current-phase.component';
import { ReleaseApprovalModalComponent } from './release-approval-modal/release-approval-modal.component';
import { ReleaseStatusComponent } from './release-status-component/release-status.component';
import { ReleaseViewModalComponent } from './release-view-modal/release-view-modal.component';
import {
  Release,
  ReleaseQuery,
  ReleaseResponse,
  ReleaseService,
} from './services/release.service';

@Component({
  selector: 'dcc-app-releases',
  templateUrl: './releases.component.html',
  styleUrls: ['./releases.component.scss'],
})
export class ReleasesComponent implements OnInit {
  numberOfReleases: number;
  pageSize = 50;
  pageSizes: number[] = [50, 100, 150, 200];
  numberOfPages = 0;
  firstPageToken = '{}';
  currentPage = 1;
  endOfSet: boolean;
  tooltipShowDelay = 0;
  tooltipHideDelay = 2000;

  releases: Release[] = [];
  filteredReleases: Release[] = [];
  query: ReleaseQuery;

  modularApp = 'Modular App';
  firmware = 'Firmware';

  columnDefs: ColDef[] = [
    {
      field: 'name',
      headerName: 'Release Name',
      maxWidth: 300,
      cellRenderer: this.modalLinkRederer.bind(this),
    },
    {
      field: 'hasPendingApproval',
      headerName: '',
      maxWidth: 129,
      minWidth: 129,
      cellRenderer: this.approvalButtonRender,
      onCellClicked: (e) => {
        if (e.data.hasPendingApproval) {
          const modal = this.modalService.open(ReleaseApprovalModalComponent, {
            size: 'lg',
            windowClass: 'lytxModal',
            centered: true,
          });
          modal.componentInstance.refreshPage.subscribe(
            async () => await this.refreshAfterNewReleases(),
          );
          modal.componentInstance.release = e.data;
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer: ReleaseStatusComponent,
    },
    {
      field: 'currentPhase',
      headerName: 'Current Phase',
      cellRenderer: CurrentPhaseComponent,
    },
    {
      field: 'cohorts',
      maxWidth: 300,
      headerName: 'Cohorts',
      tooltipComponent: CohortsColumnViewToolTipComponent,
      tooltipValueGetter: () => 'CohortsColumnViewComponent',
      cellRenderer: CohortsColumnViewComponent,
    },
    {
      field: 'apps',
      maxWidth: 300,
      headerName: 'Apps',
      tooltipComponent: AppsColumnViewComponentTooltip,
      tooltipValueGetter: () => 'AppsColumnViewComponentTooltip',
      cellRenderer: moduleRender,
    },
    { field: 'description', maxWidth: 300, headerName: 'Description' },
    { field: 'releaseCreator', headerName: 'Release Creator' },
    {
      field: 'createdDate',
      headerName: 'Creation Date',
      cellRenderer: this.createdDateRender,
    },
    {
      field: 'updatedDate',
      headerName: 'Most Recent Updated Date',
      cellRenderer: this.updatedDateRender,
    },
  ];

  defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true,
    suppressMovable: true,
  };

  private gridApi!: GridApi<Release>;

  constructor(
    private releasesService: ReleaseService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    const releaseId = this.route.snapshot.queryParams['releaseId'];
    const queryString = this.route.snapshot.queryParams['query'];

    if (releaseId) {
      this.openReleaseModalFromUrl(releaseId, queryString);
    }

    if (queryString && queryString !== '') {
      await this.parseRouteQueryParams(queryString);
    }

    this.resetPagination();
  }

  modalLinkRederer(params: any): HTMLElement {
    const link = document.createElement('a');
    link.href = '#';
    link.textContent = params.value;
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const modal = this.modalService.open(ReleaseViewModalComponent, {
        size: 'lg',
        windowClass: 'lytxModal',
        centered: true,
      });
      modal.componentInstance.refreshPage.subscribe(
        async () => await this.refreshAfterNewReleases(),
      );
      modal.componentInstance.releaseId = params.data.id;
      modal.componentInstance.releaseQuery = JSON.stringify(this.query);
    });
    return link;
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    await this.getData();

    window.addEventListener('resize', function () {
      setTimeout(function () {
        params.api.sizeColumnsToFit();
      });
    });

    this.gridApi.sizeColumnsToFit();
    params.columnApi.autoSizeAllColumns();
  }

  reset() {
    this.resetPagination();
  }

  async onRefresh() {
    this.gridApi.showLoadingOverlay();
    await this.getData();
    this.applyFilters();
    this.applyPagination();
    this.gridApi.hideOverlay();
    this.gridApi.sizeColumnsToFit();
  }

  onCreateClick() {
    const modal = this.modalService.open(CreateReleaseComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.refreshPage.subscribe(
      async () => await this.refreshAfterNewReleases(),
    );
    modal.componentInstance.closePage.subscribe(
      async () => await this.refreshAfterNewReleases(),
    );
  }

  onQuery(query: ReleaseQuery) {
    this.query = query;
    this.resetPagination();
    this.getData();
    this.setQueryParams(query);
  }

  async getData(): Promise<void> {
    const response = await this.releasesService.getReleases();
    this.updatePageSettings(response);
  }

  async refreshAfterNewReleases() {
    this.onRefresh(); //refresh immediately
  }

  onPageSizeSelected(pageSize: number): void {
    this.resetPagination();
    this.currentPage = 1;
    this.pageSize = pageSize;
    this.onRefresh();
  }

  async onPageSelection(pageNumber: number): Promise<void> {
    if (this.currentPage === pageNumber) {
      return;
    }
    this.currentPage = pageNumber;
    this.applyPagination();
  }

  private async parseRouteQueryParams(queryString: string) {
    const urlQuery = JSON.parse(queryString);
    this.query = {
      releaseName: urlQuery.releaseName,
      status: urlQuery.status,
      releaseCreator: urlQuery.releaseCreator,
      appType: urlQuery.appType,
    };
  }

  private openReleaseModalFromUrl(releaseId: string, queryString: string) {
    const modal = this.modalService.open(ReleaseViewModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.refreshPage.subscribe(
      async () => await this.refreshAfterNewReleases(),
    );
    modal.componentInstance.releaseId = releaseId;
    modal.componentInstance.releaseQuery = queryString;
  }

  private applyFilters(): void {
    this.filteredReleases = this.releases.filter(
      (c) =>
        (!this.query ||
          !this.query.releaseName ||
          c.name
            .toLowerCase()
            .includes(this.query.releaseName.toLowerCase())) &&
        (!this.query ||
          !this.query.status ||
          !this.query.status.length ||
          this.query.status
            .toString()
            .toLowerCase()
            .includes(c.status.toLowerCase())) &&
        (!this.query ||
          !this.query.releaseCreator ||
          (c.releaseCreator &&
            c.releaseCreator
              .toLowerCase()
              .includes(this.query.releaseCreator.toLowerCase()))),
    );

    const hasFirmwareFilter = this.query.appType
      .toString()
      .toLowerCase()
      .includes(this.firmware.toLowerCase());
    const hasModuleFilter = this.query.appType
      .toString()
      .toLowerCase()
      .includes(this.modularApp.toString().toLowerCase());

    if (
      (hasFirmwareFilter && hasModuleFilter) ||
      (!hasFirmwareFilter && !hasModuleFilter)
    ) {
      this.filteredReleases = [...this.filteredReleases];
    } else {
      this.filteredReleases = this.filteredReleases.filter((release) => {
        if (hasFirmwareFilter && release.firmware?.length > 0) {
          return true;
        } else if (hasModuleFilter && release.modules?.length > 0) {
          return true;
        }
        return false;
      });
    }
  }

  private applyPagination(): void {
    const totalPages = Math.ceil(this.filteredReleases.length / this.pageSize);

    this.numberOfPages = totalPages === 0 ? 1 : totalPages;

    this.currentPage = Math.max(
      1,
      Math.min(this.currentPage, this.numberOfPages),
    );

    const startIndex = (this.currentPage - 1) * this.pageSize;

    const startIdxOffset = Number(startIndex) + Number(this.pageSize);

    const endIndex = Math.min(startIdxOffset, this.filteredReleases.length);

    const pageData = this.filteredReleases.slice(startIndex, endIndex);

    this.endOfSet = this.currentPage === this.numberOfPages ? true : false;

    this.numberOfReleases = this.filteredReleases.length;

    this.gridApi.setRowData(pageData);
  }

  private updatePageSettings(response: ReleaseResponse): void {
    this.releases = response?.releases || [];

    this.applyFilters();

    this.releases = this.filteredReleases;

    const releasesSize = this.releases.length;

    if (releasesSize > this.pageSize) {
      this.endOfSet = false;

      this.numberOfPages = Math.ceil(releasesSize / this.pageSize);

      this.gridApi.setRowData(this.releases.slice(0, this.pageSize));

      return;
    }
    this.endOfSet = true;

    this.gridApi.setRowData(this.releases);
  }

  private resetPagination(): void {
    this.numberOfPages = 1;
    this.currentPage = 1;
  }

  private createdDateRender(params: any): string {
    if (
      params.data === null ||
      params.data.createdDate === null ||
      params.data.createdDate === undefined
    ) {
      return '';
    }
    return formatDate(params.data.createdDate, 'short', 'en-US');
  }

  private updatedDateRender(params: any): string {
    if (
      params.data === null ||
      params.data.lastChangeRequestDate === null ||
      params.data.lastChangeRequestDate === undefined
    ) {
      return '';
    }
    return formatDate(params.data.lastChangeRequestDate, 'short', 'en-US');
  }

  private approvalButtonRender(params: any): HTMLElement {
    if (params.data.hasPendingApproval) {
      const button = document.createElement('button');
      button.textContent = 'Review';
      button.className = 'lx-btn-small lx-btn-negative';
      button.style.cssText = 'min-width: 0px;'; // Changed to lx-btn-negative
      return button;
    }
    return null;
  }

  private setQueryParams(rawQuery: ReleaseQuery): Promise<boolean> {
    let queryString: string;
    if (Object.keys(rawQuery).length > 0) {
      queryString = JSON.stringify(rawQuery);
    }

    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: queryString ?? '',
      },
    });
  }
}

export const moduleRender = (params: any): string => {
  const modules = params.data.modules.map((m: Module) => {
    return `${m.name}-${m.version}-${m.platform}`;
  });

  const firmwares =
    params.data.firmware?.map((m: Module) => {
      if (m.name && m.version) {
        return `${m.name}-${m.version}`;
      }

      return null;
    }) ?? [];

  return [...modules, ...firmwares].filter((item) => item).join(', ');
};
